import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Logo — the Corenote mark: a thick ring broken by one gap, an agent
 * satellite sitting in that gap, and the core disc. Humans at the core,
 * agents in orbit (CORE-231).
 *
 *   <Logo />                       wordmark, 24px
 *   <Logo variant="mark" size={16} animate="once" />
 *   <Logo variant="app" size={32} />
 *
 * - Ring and wordmark take `currentColor` (default ink-100); the core is
 *   always `logo-core` (fixed in both themes) and the satellite `agent`.
 *   `mono` renders everything in currentColor for single-ink contexts.
 * - `animate="once"` draws the ring and pops the core and satellite in
 *   (≈1.4s) — app shells, sign-in, empty states; never on every
 *   navigation. `animate="loop"` spins the orbit: reserved for
 *   AgentIndicator (the agent-working mark).
 * - Never recolour the core, outline the mark, rotate it or place it on a
 *   `core` fill. Minimum sizes: 16px mark, 24px wordmark. Clear space
 *   equals the satellite's diameter on every side.
 * - `variant="app"` is the fixed-dark tile for favicons and app icons.
 *
 * Reduced motion: the static markup carries the settled state and every
 * animation utility is motion-safe, so a reduced-motion user sees the
 * finished mark immediately.
 */

/** Mark geometry (64×64 viewBox) — mirrors the reference draw. */
const MARK = {
  ringRadius: 21,
  ringStroke: 7,
  dash: 108.49,
  gap: 131.95,
  rotate: -18,
  satelliteX: 45.5,
  satelliteY: 15.91,
  satelliteR: 6,
  coreR: 11,
} as const;

/** "CORENOTE" drawn as paths (Geist-derived glyphs; no font needed). */
const WORDMARK_GLYPHS =
  '<path transform="translate(80.00 47) scale(0.04225352112676056 -0.04225352112676056)" d="M372 -16Q275 -16 201.0 28.5Q127 73 85.0 155.5Q43 238 43 354Q43 468 84.0 551.5Q125 635 199.0 680.5Q273 726 373 726Q509 726 584.0 658.5Q659 591 682 470L546 463Q533 533 490.0 573.0Q447 613 373 613Q313 613 269.0 581.0Q225 549 201.0 491.0Q177 433 177 354Q177 274 201.5 216.5Q226 159 270.0 128.0Q314 97 372 97Q451 97 494.5 140.5Q538 184 550 260L686 253Q672 170 632.0 109.5Q592 49 526.5 16.5Q461 -16 372 -16Z"/><path transform="translate(112.70 47) scale(0.04225352112676056 -0.04225352112676056)" d="M381 -16Q276 -16 200.0 28.5Q124 73 83.5 156.0Q43 239 43 354Q43 469 83.5 552.5Q124 636 200.0 681.0Q276 726 381 726Q488 726 563.5 681.0Q639 636 679.5 552.5Q720 469 720 354Q720 239 679.5 156.0Q639 73 563.5 28.5Q488 -16 381 -16ZM381 97Q446 97 491.5 127.5Q537 158 561.5 215.5Q586 273 586 354Q586 435 561.5 493.0Q537 551 491.5 582.0Q446 613 381 613Q318 613 272.0 582.0Q226 551 201.5 493.0Q177 435 177 354Q177 273 201.5 215.5Q226 158 272.0 127.5Q318 97 381 97Z"/><path transform="translate(147.06 47) scale(0.04225352112676056 -0.04225352112676056)" d="M80 0V710H377Q454 710 510.0 684.0Q566 658 597.0 610.0Q628 562 628 497Q628 448 607.0 410.0Q586 372 550.5 349.0Q515 326 472 321L467 331Q536 331 573.5 300.0Q611 269 616 205L634 0H502L487 186Q484 228 460.0 248.0Q436 268 380 268H210V0ZM210 382H369Q428 382 461.0 410.0Q494 438 494 489Q494 541 460.5 569.0Q427 597 363 597H210Z"/><path transform="translate(178.28 47) scale(0.04225352112676056 -0.04225352112676056)" d="M80 0V710H558V597H210V412H546V300H210V113H566V0Z"/><path transform="translate(206.38 47) scale(0.04225352112676056 -0.04225352112676056)" d="M80 0V710H232L538 176V710H668V0H512L210 519V0Z"/><path transform="translate(240.10 47) scale(0.04225352112676056 -0.04225352112676056)" d="M381 -16Q276 -16 200.0 28.5Q124 73 83.5 156.0Q43 239 43 354Q43 469 83.5 552.5Q124 636 200.0 681.0Q276 726 381 726Q488 726 563.5 681.0Q639 636 679.5 552.5Q720 469 720 354Q720 239 679.5 156.0Q639 73 563.5 28.5Q488 -16 381 -16ZM381 97Q446 97 491.5 127.5Q537 158 561.5 215.5Q586 273 586 354Q586 435 561.5 493.0Q537 551 491.5 582.0Q446 613 381 613Q318 613 272.0 582.0Q226 551 201.5 493.0Q177 435 177 354Q177 273 201.5 215.5Q226 158 272.0 127.5Q318 97 381 97Z"/><path transform="translate(274.45 47) scale(0.04225352112676056 -0.04225352112676056)" d="M227 0V597H11V710H572V597H357V0Z"/><path transform="translate(301.24 47) scale(0.04225352112676056 -0.04225352112676056)" d="M80 0V710H558V597H210V412H546V300H210V113H566V0Z"/>';
const WORDMARK_WIDTH = 327.23;

export interface LogoProps {
  variant?: "wordmark" | "mark" | "app";
  /** Height in px (wordmark width scales from it). */
  size?: number;
  /** `once` draws the ring and pops core/satellite in; `loop` spins the
   *  orbit (AgentIndicator only). */
  animate?: false | "once" | "loop";
  /** Single-ink version: core and satellite take currentColor. */
  mono?: boolean;
  /** Accessible name; defaults to "Corenote". */
  label?: string;
  className?: string;
}

function MarkGlyph({
  animate,
  mono,
}: {
  animate: LogoProps["animate"];
  mono?: boolean;
}) {
  const coreFill = mono ? "fill-current" : "fill-logo-core";
  const satelliteFill = mono ? "fill-current" : "fill-agent";
  return (
    <>
      <g
        className={cn(
          "origin-center",
          animate === "loop" && "motion-safe:animate-logo-orbit",
        )}
      >
        <circle
          cx="32"
          cy="32"
          r={MARK.ringRadius}
          fill="none"
          stroke="currentColor"
          strokeWidth={MARK.ringStroke}
          strokeLinecap="round"
          strokeDasharray={`${MARK.dash} ${MARK.gap}`}
          transform={`rotate(${MARK.rotate} 32 32)`}
          className={cn(animate === "once" && "motion-safe:animate-logo-draw")}
        />
        <circle
          cx={MARK.satelliteX}
          cy={MARK.satelliteY}
          r={MARK.satelliteR}
          className={cn(
            "[transform-box:fill-box] origin-center",
            satelliteFill,
            animate === "once" &&
              "motion-safe:animate-logo-pop [animation-delay:1250ms]",
          )}
        />
      </g>
      <circle
        cx="32"
        cy="32"
        r={MARK.coreR}
        className={cn(
          "origin-center",
          coreFill,
          animate === "once" &&
            "motion-safe:animate-logo-pop [animation-delay:900ms]",
          animate === "loop" && "motion-safe:animate-logo-breathe",
        )}
      />
    </>
  );
}

export function Logo({
  variant = "wordmark",
  size = 24,
  animate = false,
  mono = false,
  label = "Corenote",
  className,
  ...props
}: LogoProps & React.SVGProps<SVGSVGElement>) {
  if (variant === "app") {
    return (
      <svg
        role="img"
        aria-label={label}
        width={size}
        height={size}
        viewBox="0 0 64 64"
        className={cn("inline-block shrink-0", className)}
        {...props}
      >
        <rect width="64" height="64" rx="14" className="fill-logo-tile" />
        <g transform="translate(8 8) scale(.75)" className="text-logo-tile-ink">
          <MarkGlyph animate={animate} mono={mono} />
        </g>
      </svg>
    );
  }
  if (variant === "mark") {
    return (
      <svg
        role="img"
        aria-label={label}
        width={size}
        height={size}
        viewBox="0 0 64 64"
        className={cn("inline-block shrink-0 text-ink-100", className)}
        {...props}
      >
        <MarkGlyph animate={animate} mono={mono} />
      </svg>
    );
  }
  const width = Math.round((size * (WORDMARK_WIDTH + 2)) / 64);
  return (
    <svg
      role="img"
      aria-label={label}
      width={width}
      height={size}
      viewBox={`0 0 ${WORDMARK_WIDTH + 2} 64`}
      className={cn("inline-block shrink-0 text-ink-100", className)}
      {...props}
    >
      <MarkGlyph animate={animate} mono={mono} />
      <g
        fill="currentColor"
        dangerouslySetInnerHTML={{ __html: WORDMARK_GLYPHS }}
      />
    </svg>
  );
}
